import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

const RASHI_GLYPHS = ['\u2648', '\u2649', '\u264A', '\u264B', '\u264C', '\u264D', '\u264E', '\u264F', '\u2650', '\u2651', '\u2652', '\u2653'];

export const TwinkleField = () => {
  const stars = useMemo(
    () => Array.from({ length: 34 }, (_, i) => ({
      left: (i * 37) % 100,
      top: (i * 53) % 100,
      size: 2 + (i % 4),
      delay: (i % 7) * 0.45,
      duration: 2.6 + (i % 5) * 0.6,
    })),
    []
  );
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {stars.map((s, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: s.size,
            height: s.size,
            background: i % 3 === 0 ? '#C84B32' : '#F5B942',
            boxShadow: '0 0 8px rgba(245,185,66,0.8)',
          }}
          animate={{ opacity: [0.15, 1, 0.15], scale: [0.6, 1.2, 0.6] }}
          transition={{ duration: s.duration, delay: s.delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
};

const MandalaRings = () => (
  <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full">
    <defs>
      <linearGradient id="goldStroke" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#F5B942" />
        <stop offset="100%" stopColor="#C84B32" />
      </linearGradient>
    </defs>
    <circle cx="200" cy="200" r="196" fill="none" stroke="url(#goldStroke)" strokeWidth="1.2" opacity="0.7" />
    <circle cx="200" cy="200" r="168" fill="none" stroke="#F5B942" strokeWidth="0.8" strokeDasharray="3 6" opacity="0.7" />
    {[...Array(12)].map((_, i) => (
      <line
        key={i}
        x1="200" y1="4" x2="200" y2="32"
        stroke="url(#goldStroke)" strokeWidth="1"
        transform={`rotate(${i * 30} 200 200)`}
        opacity="0.8"
      />
    ))}
    {[...Array(16)].map((_, i) => (
      <ellipse
        key={`p-${i}`}
        cx="200" cy="200" rx="120" ry="34"
        fill="none" stroke="#F5B942" strokeWidth="0.6" opacity="0.35"
        transform={`rotate(${i * 11.25} 200 200)`}
      />
    ))}
  </svg>
);

const RashiRing = () => (
  <div className="absolute inset-0">
    {RASHI_GLYPHS.map((g, i) => {
      const angle = (i * 30 + 15) * (Math.PI / 180);
      const x = 50 + 45.5 * Math.sin(angle);
      const y = 50 - 45.5 * Math.cos(angle);
      return (
        <span
          key={g}
          className="absolute -translate-x-1/2 -translate-y-1/2 text-[13px] sm:text-base font-semibold"
          style={{ left: `${x}%`, top: `${y}%`, color: i % 2 ? '#C84B32' : '#B7791F', fontFamily: 'serif' }}
        >
          {g}{'\uFE0E'}
        </span>
      );
    })}
  </div>
);

const Orbit = ({ size, duration, color, dot, reverse }) => (
  <motion.div
    className="absolute left-1/2 top-1/2 rounded-full border"
    style={{ width: size, height: size, marginLeft: -size / 2, marginTop: -size / 2, borderColor: 'rgba(200,75,50,0.14)' }}
    animate={{ rotate: reverse ? -360 : 360 }}
    transition={{ duration, repeat: Infinity, ease: 'linear' }}
  >
    <span
      className="absolute left-1/2 -top-[1px] -translate-x-1/2 -translate-y-1/2 rounded-full"
      style={{ width: dot, height: dot, background: color, boxShadow: `0 0 14px ${color}` }}
    />
  </motion.div>
);

export const ZodiacMandala = ({ className = '', showGlyphs = true }) => (
  <div className={`relative aspect-square ${className}`} data-testid="zodiac-mandala">
    <motion.div
      className="absolute inset-0 rounded-full"
      style={{ background: 'radial-gradient(circle, rgba(245,185,66,0.28) 0%, rgba(252,233,217,0.4) 45%, transparent 70%)' }}
      animate={{ scale: [1, 1.06, 1], opacity: [0.8, 1, 0.8] }}
      transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
    />
    <motion.div className="absolute inset-0" animate={{ rotate: 360 }} transition={{ duration: 120, repeat: Infinity, ease: 'linear' }}>
      <MandalaRings />
      {showGlyphs && <RashiRing />}
    </motion.div>
    <Orbit size={190} duration={14} color="#C84B32" dot={10} />
    <Orbit size={240} duration={22} color="#F5B942" dot={14} reverse />
    <Orbit size={150} duration={9} color="#76B7F2" dot={7} />
    <div className="absolute inset-0 flex items-center justify-center">
      <motion.div
        className="relative w-[34%] aspect-square rounded-full flex items-center justify-center"
        style={{
          background: 'linear-gradient(140deg, #F5B942 0%, #E8913A 45%, #C84B32 100%)',
          boxShadow: '0 20px 60px rgba(200,75,50,0.35), inset 0 2px 10px rgba(255,255,255,0.4)'
        }}
        initial={{ scale: 0, rotate: -90 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 120, damping: 14, delay: 0.2 }}
      >
        <span className="text-white text-5xl sm:text-6xl leading-none font-['Fraunces']" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.18)' }}>
          ॐ
        </span>
      </motion.div>
    </div>
  </div>
);
